import { LoaderCircle, RefreshCw } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useOutletContext } from "react-router-dom";

import { ToastViewport, type ToastState } from "@/components/ToastViewport";
import { useSession } from "@/context/SessionContext";
import {
  createBlankAgreementFormSchema,
  createDefaultAgreementFormSchema,
  normalizeAgreementFormSchema,
  type AgreementFormSchema
} from "@/lib/agreement-form-schema";
import {
  deleteAdminCandidate,
  deleteTemplateVersion,
  fetchAdminDashboard,
  fetchAdminSubmissionDetail,
  publishTemplateVersion,
  reviewAgreementSubmission,
  saveTemplateDraft,
  updateAdminCandidate,
  updateTemplateDraft,
  updateCandidateApproval,
  type AccountStatus,
  type DashboardResponse,
  type TemplateVersion
} from "@/lib/api";

const ADMIN_TEMPLATE_OPTIONS = [
  { slug: "internship-agreement-india", label: "Internship Agreement" },
  { slug: "addendum-offer-letter-india", label: "Addendum Agreement" }
] as const;

export interface AdminTemplateForm {
  agreementBody: string;
  companyAddress: string;
  companyName: string;
  dataProtectionNotice: string;
  defaultConfidentialityPeriod: string;
  defaultJurisdictionCity: string;
  defaultWorkingHours: string;
  description: string;
  formSchema: AgreementFormSchema;
  termsAndConditions: string;
  title: string;
}

export type AdminSubmissionDetail = Awaited<ReturnType<typeof fetchAdminSubmissionDetail>>;

export interface AdminWorkspaceContextValue {
  allUsers: DashboardResponse["users"];
  approvedUsers: DashboardResponse["users"];
  dashboard: DashboardResponse;
  draftTemplateVersions: NonNullable<DashboardResponse["template"]["versions"]>;
  handleApproval: (candidateId: string, decision: "approved" | "rejected") => Promise<void>;
  handleCreateTemplateDraft: () => Promise<void>;
  handleDeleteCandidate: (candidateId: string) => Promise<void>;
  handleDeleteTemplateVersion: (versionId: string) => Promise<void>;
  handleSubmissionDecision: (
    submissionId: string,
    decision: "approved" | "rejected",
    rejectionReason?: string,
    comment?: string,
    adminFields?: {
      companyAddress?: string;
      authorizedSignatoryName?: string;
      authorizedSignatoryDesignation?: string;
      employeeName?: string;
    }
  ) => Promise<void>;
  handlePublishTemplate: (payload?: {
    candidateIds?: string[];
    publishScope?: "all_approved_users" | "selected_users";
  }) => Promise<void>;
  handleUpdateCandidate: (
    candidateId: string,
    payload: {
      email: string;
      fullName: string;
      phone?: string;
      status: AccountStatus;
    }
  ) => Promise<void>;
  handleUpdateTemplateDraft: (versionId: string) => Promise<void>;
  isDeletingCandidateId: string | null;
  isDeletingTemplateVersionId: string | null;
  isPublishingTemplate: boolean;
  isSavingTemplate: boolean;
  isUpdating: string | null;
  loadDashboard: () => Promise<void>;
  pendingUsers: DashboardResponse["users"];
  publishedTemplateVersions: NonNullable<DashboardResponse["template"]["versions"]>;
  selectedSubmission: AdminSubmissionDetail | null;
  selectedSubmissionId: string | null;
  selectedTemplateVersion: TemplateVersion;
  selectSubmission: (submissionId: string) => Promise<void>;
  setTemplateForm: Dispatch<SetStateAction<AdminTemplateForm | null>>;
  setTemplateVersionIdToPublish: Dispatch<SetStateAction<string>>;
  templateForm: AdminTemplateForm;
  templateVersionIdToPublish: string;
}

function buildTemplateForm(version: TemplateVersion): AdminTemplateForm {
  return {
    title: version.title,
    description: version.description ?? "",
    agreementBody: version.agreementBody,
    termsAndConditions: version.termsAndConditions,
    dataProtectionNotice: version.dataProtectionNotice,
    companyName: version.companyName,
    companyAddress: version.companyAddress,
    defaultWorkingHours: version.defaults.workingHours,
    defaultConfidentialityPeriod: version.defaults.confidentialityPeriod,
    defaultJurisdictionCity: version.defaults.jurisdictionCity,
    formSchema: version.formSchema
      ? normalizeAgreementFormSchema(version.formSchema)
      : createDefaultAgreementFormSchema()
  };
}

function buildEmptyTemplateForm(): AdminTemplateForm {
  return {
    title: "",
    description: "",
    agreementBody: "",
    termsAndConditions: "",
    dataProtectionNotice: "",
    companyName: "",
    companyAddress: "",
    defaultWorkingHours: "",
    defaultConfidentialityPeriod: "",
    defaultJurisdictionCity: "",
    formSchema: createBlankAgreementFormSchema()
  };
}

export function useAdminWorkspace() {
  return useOutletContext<AdminWorkspaceContextValue>();
}

export function AdminWorkspaceLayout() {
  const { session } = useSession();
  const location = useLocation();
  const [templateSlug] = useState<(typeof ADMIN_TEMPLATE_OPTIONS)[number]["slug"]>(
    "internship-agreement-india"
  );
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<AdminSubmissionDetail | null>(null);
  const [templateForm, setTemplateForm] = useState<AdminTemplateForm | null>(null);
  const [templateVersionIdToPublish, setTemplateVersionIdToPublish] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isPublishingTemplate, setIsPublishingTemplate] = useState(false);
  const [isDeletingCandidateId, setIsDeletingCandidateId] = useState<string | null>(null);
  const [isDeletingTemplateVersionId, setIsDeletingTemplateVersionId] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    void loadDashboard();
  }, [templateSlug]);

  const requestedTemplateVersionId = useMemo(
    () => new URLSearchParams(location.search).get("versionId"),
    [location.search]
  );

  const selectedTemplateVersion = useMemo(() => {
    if (!dashboard) {
      return null;
    }

    return (
      dashboard.template.versions?.find((version) => version.id === requestedTemplateVersionId) ??
      dashboard.template.activeVersion
    );
  }, [dashboard, requestedTemplateVersionId]);

  useEffect(() => {
    if (!dashboard || !selectedTemplateVersion) {
      return;
    }

    setTemplateForm(
      location.pathname === "/admin/create-form"
        ? buildEmptyTemplateForm()
        : buildTemplateForm(selectedTemplateVersion)
    );
    setTemplateVersionIdToPublish(
      location.pathname === "/admin/create-form"
        ? dashboard.template.versions?.find((version) => version.status === "draft")?.id ?? ""
        : (selectedTemplateVersion.status === "draft" ? selectedTemplateVersion.id : undefined) ??
            dashboard.template.versions?.find((version) => version.status === "draft")?.id ??
            dashboard.template.activeVersion.id
    );
  }, [dashboard, location.pathname, selectedTemplateVersion]);

  useEffect(() => {
    const submissions = dashboard?.submissions ?? [];
    const firstSubmission = submissions[0];

    if (!firstSubmission) {
      setSelectedSubmissionId(null);
      setSelectedSubmission(null);
      return;
    }

    if (selectedSubmissionId) {
      return;
    }

    const nextSubmissionId = firstSubmission.id;

    setSelectedSubmissionId(nextSubmissionId);
    void loadSubmissionDetail(nextSubmissionId);
  }, [dashboard?.submissions, selectedSubmissionId]);

  async function loadDashboard() {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchAdminDashboard();
      setDashboard(response);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to load dashboard.");
    } finally {
      setIsLoading(false);
    }
  }

  async function loadSubmissionDetail(submissionId: string) {
    try {
      const response = await fetchAdminSubmissionDetail(submissionId);
      setSelectedSubmission(response);
      setSelectedSubmissionId(submissionId);
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to load submission detail."
      );
    }
  }

  async function handleApproval(candidateId: string, decision: "approved" | "rejected") {
    setIsUpdating(candidateId);
    setError(null);

    try {
      const response = await updateCandidateApproval(candidateId, { decision });
      setToast({
        tone: "success",
        title: "Candidate updated",
        message: response.message
      });
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to update candidate status."
      );
    } finally {
      setIsUpdating(null);
    }
  }

  async function handleUpdateCandidate(
    candidateId: string,
    payload: {
      email: string;
      fullName: string;
      phone?: string;
      status: AccountStatus;
    }
  ) {
    setIsUpdating(candidateId);
    setError(null);

    try {
      const response = await updateAdminCandidate(candidateId, payload);
      setToast({
        tone: "success",
        title: "Candidate updated",
        message: response.message
      });
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to update candidate."
      );
      throw requestError;
    } finally {
      setIsUpdating(null);
    }
  }

  async function handleDeleteCandidate(candidateId: string) {
    setIsDeletingCandidateId(candidateId);
    setError(null);

    try {
      const response = await deleteAdminCandidate(candidateId);
      setToast({
        tone: "success",
        title: "Candidate deleted",
        message: response.message
      });
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to delete candidate."
      );
    } finally {
      setIsDeletingCandidateId(null);
    }
  }

  async function handleDeleteTemplateVersion(versionId: string) {
    setIsDeletingTemplateVersionId(versionId);
    setError(null);

    try {
      const response = await deleteTemplateVersion(versionId);
      setToast({
        tone: "success",
        title: "Form deleted",
        message: response.message
      });
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to delete form version."
      );
    } finally {
      setIsDeletingTemplateVersionId(null);
    }
  }

  async function handleSubmissionDecision(
    submissionId: string,
    decision: "approved" | "rejected",
    rejectionReason?: string,
    comment?: string,
    adminFields?: {
      companyAddress?: string;
      authorizedSignatoryName?: string;
      authorizedSignatoryDesignation?: string;
      employeeName?: string;
    }
  ) {
    setIsUpdating(submissionId);
    setError(null);

    try {
      const response = await reviewAgreementSubmission(submissionId, {
        decision,
        ...(adminFields ?? {}),
        rejectionReason,
        comment
      });
      setToast({
        tone: "success",
        title: "Agreement updated",
        message: response.message
      });
      await loadDashboard();
      if (selectedSubmissionId === submissionId) {
        await loadSubmissionDetail(submissionId);
      }
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to review agreement."
      );
      throw requestError;
    } finally {
      setIsUpdating(null);
    }
  }

  async function handleCreateTemplateDraft() {
    if (!templateForm) {
      return;
    }

    setIsSavingTemplate(true);
    setError(null);

    try {
      const response = await saveTemplateDraft(templateForm, templateSlug);
      setToast({
        tone: "success",
        title: "Template draft saved",
        message: response.message
      });
      setTemplateVersionIdToPublish(response.version.id);
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to save template draft."
      );
    } finally {
      setIsSavingTemplate(false);
    }
  }

  async function handleUpdateTemplateDraft(versionId: string) {
    if (!templateForm) {
      return;
    }

    setIsSavingTemplate(true);
    setError(null);

    try {
      const response = await updateTemplateDraft(versionId, templateForm);
      setToast({
        tone: "success",
        title: "Template draft updated",
        message: response.message
      });
      setTemplateVersionIdToPublish(response.version.id);
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to update template draft."
      );
    } finally {
      setIsSavingTemplate(false);
    }
  }

  async function handlePublishTemplate(payload?: {
    candidateIds?: string[];
    publishScope?: "all_approved_users" | "selected_users";
  }) {
    if (!templateVersionIdToPublish) {
      return;
    }

    setIsPublishingTemplate(true);
    setError(null);

    try {
      const response = await publishTemplateVersion(templateVersionIdToPublish, payload);
      setToast({
        tone: "success",
        title: "Template published",
        message: response.message
      });
      await loadDashboard();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to publish template version."
      );
    } finally {
      setIsPublishingTemplate(false);
    }
  }

  const allUsers = useMemo(
    () => dashboard?.users ?? [],
    [dashboard]
  );
  const pendingUsers = useMemo(
    () => dashboard?.users.filter((user) => user.status === "pending_approval") ?? [],
    [dashboard]
  );
  const approvedUsers = useMemo(
    () => dashboard?.users.filter((user) => user.status === "approved") ?? [],
    [dashboard]
  );
  const draftTemplateVersions = useMemo<NonNullable<DashboardResponse["template"]["versions"]>>(
    () => dashboard?.template.versions?.filter((version) => version.status === "draft") ?? [],
    [dashboard]
  );
  const publishedTemplateVersions = useMemo<
    NonNullable<DashboardResponse["template"]["versions"]>
  >(
    () => dashboard?.template.versions?.filter((version) => version.status === "published") ?? [],
    [dashboard]
  );

  if (isLoading) {
    return (
      <section className="mx-auto max-w-7xl px-6 py-12">
        <ToastViewport onDismiss={() => setToast(null)} toast={toast} />
        <div className="surface flex min-h-[45vh] items-center justify-center gap-3 p-8 text-slate">
          <LoaderCircle className="h-5 w-5 animate-spin" />
          Loading admin workspace
        </div>
      </section>
    );
  }

  if (!dashboard || !templateForm || !selectedTemplateVersion) {
    return (
      <section className="mx-auto max-w-7xl px-6 py-12">
        <ToastViewport onDismiss={() => setToast(null)} toast={toast} />
        <div className="surface px-7 py-8 md:px-8 md:py-9">
          <p className="eyebrow">Admin Workspace</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ink">
            Unable to load the admin panel
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate">
            {error ?? "Something blocked the admin workspace from loading."}
          </p>
          <button className="btn-secondary mt-6" onClick={() => void loadDashboard()} type="button">
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </section>
    );
  }

  const contextValue: AdminWorkspaceContextValue = {
    allUsers,
    approvedUsers,
    dashboard,
    draftTemplateVersions,
    handleApproval,
    handleCreateTemplateDraft,
    handleDeleteCandidate,
    handleDeleteTemplateVersion,
    handleSubmissionDecision,
    handlePublishTemplate,
    handleUpdateCandidate,
    handleUpdateTemplateDraft,
    isDeletingCandidateId,
    isDeletingTemplateVersionId,
    isPublishingTemplate,
    isSavingTemplate,
    isUpdating,
    loadDashboard,
    pendingUsers,
    publishedTemplateVersions,
    selectedSubmission,
    selectedSubmissionId,
    selectedTemplateVersion,
    selectSubmission: loadSubmissionDetail,
    setTemplateForm,
    setTemplateVersionIdToPublish,
    templateForm,
    templateVersionIdToPublish
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10" data-testid="admin-page">
      <ToastViewport onDismiss={() => setToast(null)} toast={toast} />

      {session?.role === "admin" && session.user.mustChangePassword ? (
        <div
          className="rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900"
          data-testid="admin-must-change-password-alert"
        >
          This admin account must change its password. Go to{" "}
          <Link className="font-semibold underline" to="/profile">
            the profile page
          </Link>{" "}
          before continuing.
        </div>
      ) : null}

      {error ? (
        <div
          className="mt-6 rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700"
          data-testid="admin-error-message"
        >
          {error}
        </div>
      ) : null}

      <Outlet context={contextValue} />
    </section>
  );
}
